/**
 * Valuation Position Utilities
 *
 * Resolves overlapping label positions on valuation gradient bars by spreading
 * labels apart symmetrically while keeping triangle markers at true positions.
 * Also aligns marker positions when different raw values round to the same
 * display string, preventing visual misalignment (SE-3836).
 */

/**
 * Resolve overlapping label positions by spreading them apart symmetrically.
 * Triangle markers stay at original positions; only label text positions adjust.
 * @param minPos - Position percentage of the lower bound label
 * @param maxPos - Position percentage of the upper bound label
 * @param minSeparation - Minimum gap between label positions (default 20%)
 * @param edgeMin - Minimum allowed position (default 5%)
 * @param edgeMax - Maximum allowed position (default 95%)
 * @returns Adjusted positions for lower and upper bound labels
 */
export const resolveOverlappingPositions = (
  minPos: number,
  maxPos: number,
  minSeparation = 20,
  edgeMin = 5,
  edgeMax = 95
): { adjustedMinPos: number; adjustedMaxPos: number } => {
  const gap = maxPos - minPos;
  if (gap >= minSeparation) {
    return { adjustedMinPos: minPos, adjustedMaxPos: maxPos };
  }

  const midpoint = (minPos + maxPos) / 2;
  const halfSep = minSeparation / 2;
  const rawMin = midpoint - halfSep;
  const rawMax = midpoint + halfSep;

  const clampedMin = Math.max(edgeMin, rawMin);
  const clampedMax = Math.min(edgeMax, rawMax);

  // If clamping on one side reduced separation, push the other side out
  const adjustedMinPos =
    clampedMax - clampedMin < minSeparation && clampedMin === edgeMin
      ? edgeMin
      : clampedMax - clampedMin < minSeparation && clampedMax === edgeMax
        ? Math.max(edgeMin, edgeMax - minSeparation)
        : clampedMin;

  const adjustedMaxPos =
    clampedMax - clampedMin < minSeparation && clampedMin === edgeMin
      ? Math.min(edgeMax, edgeMin + minSeparation)
      : clampedMax;

  return { adjustedMinPos, adjustedMaxPos };
};

/**
 * Aligns public valuation position to match lower/upper bound position when
 * their formatted display values are identical. Prevents visual misalignment
 * caused by different raw values that round to the same display string.
 * @param publicPosition - Calculated percentage position for public valuation
 * @param minPosition - Calculated percentage position for lower bound
 * @param maxPosition - Calculated percentage position for upper bound
 * @param publicValue - Raw public valuation number
 * @param minValue - Raw lower bound number
 * @param maxValue - Raw upper bound number
 * @param formatValue - Formatter that produces the display label from a raw value
 * @returns Aligned position that matches the corresponding bound when labels are identical
 */
export const alignPublicPosition = (
  publicPosition: number,
  minPosition: number,
  maxPosition: number,
  publicValue: number | null | undefined,
  minValue: number | null | undefined,
  maxValue: number | null | undefined,
  formatValue: (value: number) => string
): number => {
  if (publicValue == null) return publicPosition;
  const publicLabel = formatValue(publicValue);
  if (minValue != null && formatValue(minValue) === publicLabel) {
    return minPosition;
  }
  if (maxValue != null && formatValue(maxValue) === publicLabel) {
    return maxPosition;
  }
  return publicPosition;
};
